import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Camera, Upload, Sparkle, ArrowLeft, CheckCircle, Eye,
  User, MapPin, Phone, EnvelopeSimple, Package, FilePdf, BookmarkSimple, X, PencilSimple
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import { API } from '../config/api';
import NewClientDialog from '../components/NewClientDialog';

// Must match MAX_SURVEY_FILES in backend/routes/ai.py
const MAX_FILES = 8;

export default function AISurveyPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  // Files of one survey: [{ id, file, preview }] — preview is an object URL or 'pdf'
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [learnQueue, setLearnQueue] = useState([]); // unknown codes not yet decided
  const [learnOpen, setLearnOpen] = useState(false);
  const [savingLearn, setSavingLearn] = useState(false);

  // Correction flow
  const [correctOpen, setCorrectOpen] = useState(false);
  const [correctIndex, setCorrectIndex] = useState(null);
  const [correctOriginal, setCorrectOriginal] = useState(null);
  const [correctDraft, setCorrectDraft] = useState(null);
  const [correctNote, setCorrectNote] = useState('');
  const [savingCorrect, setSavingCorrect] = useState(false);

  // Inline "create/replace client" flow
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [linkingClient, setLinkingClient] = useState(false);

  const isClientMissing = (() => {
    const name = (result?.client?.name || '').trim().toLowerCase();
    if (!name) return true;
    // Backend defaults to "Cliente da sopralluogo" when AI couldn't read a name
    return name === 'cliente da sopralluogo';
  })();

  // Link a (newly created or pre-filled) client to the AI-generated quote.
  // Updates the quote's client_id/name/email/phone/address server-side and refreshes UI.
  const linkClientToQuote = async (clientObj) => {
    if (!result?.quote?.id || !clientObj?.id) return;
    setLinkingClient(true);
    try {
      await axios.put(`${API}/quotes/${result.quote.id}`, {
        client_id: clientObj.id,
        client_name: clientObj.name || '',
        client_email: clientObj.email || '',
        client_phone: clientObj.phone || '',
        client_address: clientObj.address || '',
      });
      setResult(prev => prev ? ({
        ...prev,
        client: {
          id: clientObj.id,
          name: clientObj.name || '',
          email: clientObj.email || '',
          phone: clientObj.phone || '',
          address: clientObj.address || '',
        }
      }) : prev);
      toast.success('Cliente collegato al preventivo');
    } catch (err) {
      console.error('Link client failed:', err);
      toast.error(err.response?.data?.detail || 'Errore nel collegare il cliente');
    } finally {
      setLinkingClient(false);
    }
  };

  const openCorrect = (idx) => {
    const item = (result?.quote?.items || [])[idx];
    if (!item) return;
    const base = {
      product_name: item.product_name || '',
      product_type: item.product_type || '',
      num_ante: item.num_ante || 0,
      width: item.width || 0,
      height: item.height || 0,
      opening_type: item.opening_type || '',
      raw_code: item.raw_code || '',
      quantity: item.quantity || 1,
      material: item.material || '',
    };
    setCorrectIndex(idx);
    setCorrectOriginal(base);
    setCorrectDraft({ ...base });
    setCorrectNote('');
    setCorrectOpen(true);
  };

  const saveCorrection = async () => {
    if (!correctOriginal || !correctDraft) return;
    setSavingCorrect(true);
    try {
      // Updates the saved quote (item, price, totals) and teaches the AI in one call
      const { data } = await axios.post(
        `${API}/ai/quotes/${result.quote.id}/items/${correctIndex}/correct`,
        { corrected: correctDraft, note: correctNote || null }
      );
      setResult(prev => {
        if (!prev) return prev;
        const items = [...(prev.quote.items || [])];
        items[correctIndex] = data.item;
        const pricing = {
          listino: items.filter(i => i.price_source === 'listino').length,
          documento: items.filter(i => i.price_source === 'documento').length,
          da_completare: items.filter(i => i.price_source === 'da_completare').length,
        };
        return { ...prev, pricing, quote: { ...prev.quote, ...data.quote, items } };
      });
      toast.success(data.lesson_saved
        ? `Preventivo aggiornato. L'IA ha imparato la correzione (${data.total_lessons} in totale)`
        : 'Nessuna modifica');
      setCorrectOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio della correzione');
    } finally {
      setSavingCorrect(false);
    }
  };

  // Client-side image compression: resize to max 2000px and re-encode JPEG ~0.85
  // Greatly reduces upload size for mobile camera photos (5-12MB -> ~300-900KB)
  const compressImage = (file) => new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('read_failed'));
      reader.onload = () => {
        const img = new Image();
        img.onerror = () => reject(new Error('image_decode_failed'));
        img.onload = () => {
          const MAX = 2000;
          let { width, height } = img;
          if (width > MAX || height > MAX) {
            if (width >= height) {
              height = Math.round((height * MAX) / width);
              width = MAX;
            } else {
              width = Math.round((width * MAX) / height);
              height = MAX;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => {
              if (!blob) return reject(new Error('blob_failed'));
              const compressed = new File(
                [blob],
                (file.name || 'survey').replace(/\.[^.]+$/, '') + '.jpg',
                { type: 'image/jpeg' }
              );
              resolve(compressed);
            },
            'image/jpeg',
            0.85
          );
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    } catch (e) {
      reject(e);
    }
  });

  const handleFileSelect = (e) => {
    let picked = Array.from(e.target.files || []);
    e.target.value = ''; // allow picking the same file again after removing it
    if (picked.length === 0) return;
    const room = MAX_FILES - selectedFiles.length;
    if (picked.length > room) {
      toast.error(t('aiSurvey.maxFiles', { max: MAX_FILES }));
      picked = picked.slice(0, Math.max(room, 0));
    }

    const valid = [];
    for (const file of picked) {
      const isImage = (file.type || '').startsWith('image/');
      const isPdf = file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf');
      if (!isImage && !isPdf) {
        toast.error(t('aiSurvey.selectValidFile'));
        continue;
      }
      if (file.size > 25 * 1024 * 1024) {
        toast.error(t('aiSurvey.fileTooLarge'));
        continue;
      }
      valid.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        preview: isImage ? URL.createObjectURL(file) : 'pdf',
      });
    }
    if (valid.length === 0) return;

    setSelectedFiles(prev => [...prev, ...valid]);
    setResult(null);
  };

  const removeFile = (id) => {
    const target = selectedFiles.find(f => f.id === id);
    if (target && target.preview !== 'pdf') URL.revokeObjectURL(target.preview);
    setSelectedFiles(prev => prev.filter(f => f.id !== id));
  };

  const handleAnalyze = async () => {
    if (selectedFiles.length === 0) {
      toast.error(t('aiSurvey.selectAnImage'));
      return;
    }
    setAnalyzing(true);
    try {
      // Compress images client-side (skip PDFs)
      const toSend = [];
      for (const { file } of selectedFiles) {
        let fileToSend = file;
        if ((file.type || '').startsWith('image/')) {
          try {
            fileToSend = await compressImage(file);
          } catch (compErr) {
            console.warn('Compression failed, sending original:', compErr);
          }
        }
        // Safety net: if still > 9MB after compression, refuse and ask user
        if (fileToSend.size > 9 * 1024 * 1024) {
          toast.error(t('aiSurvey.fileTooLarge'));
          setAnalyzing(false);
          return;
        }
        toSend.push(fileToSend);
      }
      if (toSend.reduce((sum, f) => sum + f.size, 0) > 19 * 1024 * 1024) {
        toast.error(t('aiSurvey.totalTooLarge'));
        setAnalyzing(false);
        return;
      }

      const formData = new FormData();
      toSend.forEach(f => formData.append('files', f));
      const { data } = await axios.post(`${API}/ai/analyze-survey`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000 // 3 min — Anthropic vision can take 10-30s + slow mobile uploads
      });
      setResult(data || {})
      toast.success(data.message);
      // If AI found unknown codes, offer user to teach them
      const unknowns = (data.unknown_codes || []).map(u => ({
        code: u.code,
        name: u.suggested_name || '',
        product_type: u.suggested_product_type || '',
        num_ante: u.suggested_num_ante || 0,
      }));
      if (unknowns.length > 0) {
        setLearnQueue(unknowns);
        setLearnOpen(true);
      }
    } catch (err) {
      console.error('AI analysis error:', err);
      const detail = err.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
        toast.error("Connessione lenta o timeout. Riprova con una connessione migliore o una foto più piccola.");
      } else if (!err.response) {
        toast.error("Errore di rete. Controlla la connessione e riprova.");
      } else {
        toast.error(t('aiSurvey.analyzeError'));
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleReset = () => {
    selectedFiles.forEach(f => f.preview !== 'pdf' && URL.revokeObjectURL(f.preview));
    setSelectedFiles([]);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const formatDim = (val) => {
    if (!val || val === 0) return '-';
    return Number.isInteger(val) ? val : Math.round(val);
  };

  const saveLearnedVocab = async () => {
    const toSave = learnQueue.filter(v => v.name && v.name.trim());
    if (toSave.length === 0) {
      setLearnOpen(false);
      setLearnQueue([]);
      return;
    }
    setSavingLearn(true);
    let added = 0;
    for (const v of toSave) {
      try {
        const { data } = await axios.post(`${API}/settings/ai-vocabulary/add`, {
          code: v.code,
          name: v.name.trim(),
          product_type: v.product_type || null,
          num_ante: parseInt(v.num_ante) || 0,
        });
        if (data.added) added += 1;
      } catch (err) {
        console.error('Errore learn:', err);
      }
    }
    setSavingLearn(false);
    setLearnOpen(false);
    setLearnQueue([]);
    if (added > 0) toast.success(`${added} ${added === 1 ? 'sigla aggiunta' : 'sigle aggiunte'} al vocabolario`);
  };

  return (
    <div className="space-y-6" data-testid="ai-survey-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/quotes')} className="text-[#4A5568]" data-testid="back-button">
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-bold text-[#0A0F1A] flex items-center gap-3">
            <Sparkle size={28} className="text-[#0033A0]" weight="fill" />
            {t('aiSurvey.title')}
          </h1>
          <p className="text-[#4A5568] mt-1">
            {t('aiSurvey.subtitle')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload Section */}
        <Card className="border border-[#E2E8F0] rounded-sm">
          <CardHeader className="border-b border-[#E2E8F0] pb-4">
            <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A] flex items-center gap-2">
              <Camera size={20} className="text-[#0033A0]" />
              {t('aiSurvey.uploadImage')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*,.pdf,application/pdf"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            <input
              type="file"
              ref={cameraInputRef}
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="camera-input"
            />
            
            {selectedFiles.length === 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => cameraInputRef.current?.click()}
                    className="h-48 border-2 border-dashed border-[#0033A0] rounded-sm bg-[#F8FAFF] hover:bg-[#E8F0FE] transition-colors flex flex-col items-center justify-center gap-3 cursor-pointer"
                    data-testid="camera-area"
                  >
                    <div className="w-14 h-14 bg-[#0033A0] rounded-full flex items-center justify-center">
                      <Camera size={26} className="text-white" weight="fill" />
                    </div>
                    <div className="text-center">
                      <p className="font-medium text-[#0A0F1A]">{t('aiSurvey.takePhoto')}</p>
                      <p className="text-xs text-[#4A5568] mt-1">{t('aiSurvey.openCamera')}</p>
                    </div>
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="h-48 border-2 border-dashed border-[#E2E8F0] rounded-sm bg-white hover:bg-[#F4F6F8] transition-colors flex flex-col items-center justify-center gap-3 cursor-pointer"
                    data-testid="upload-area"
                  >
                    <div className="w-14 h-14 bg-[#4A5568] rounded-full flex items-center justify-center">
                      <Upload size={26} className="text-white" />
                    </div>
                    <div className="text-center">
                      <p className="font-medium text-[#0A0F1A]">{t('aiSurvey.uploadFromGallery')}</p>
                      <p className="text-xs text-[#4A5568] mt-1">{t('aiSurvey.fromGallery')}</p>
                    </div>
                  </button>
                </div>
                <p className="text-xs text-center text-[#4A5568]">{t('aiSurvey.formatsHint', { max: MAX_FILES })}</p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-[#4A5568]" data-testid="files-count">
                  {t('aiSurvey.filesSelected', { count: selectedFiles.length, max: MAX_FILES })}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="files-grid">
                  {selectedFiles.map((f, idx) => (
                    <div key={f.id} className="relative rounded-sm overflow-hidden border border-[#E2E8F0] bg-[#F4F6F8]" data-testid={`file-thumb-${idx}`}>
                      {f.preview === 'pdf' ? (
                        <div className="w-full h-32 flex flex-col items-center justify-center gap-1 px-2">
                          <FilePdf size={40} className="text-red-500" weight="duotone" />
                          <p className="text-xs font-medium text-[#0A0F1A] truncate max-w-full">{f.file.name}</p>
                          <p className="text-[10px] text-[#4A5568]">{(f.file.size / 1024).toFixed(0)} KB</p>
                        </div>
                      ) : (
                        <img src={f.preview} alt={`${t('aiSurvey.preview')} ${idx + 1}`} className="w-full h-32 object-cover" />
                      )}
                      <button
                        type="button"
                        onClick={() => removeFile(f.id)}
                        disabled={analyzing}
                        className="absolute top-1 right-1 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                        aria-label={t('aiSurvey.removeFile')}
                        data-testid={`remove-file-${idx}`}
                      >
                        <X size={14} weight="bold" />
                      </button>
                    </div>
                  ))}
                  {selectedFiles.length < MAX_FILES && !analyzing && (
                    <div className="h-32 grid grid-rows-2 gap-2">
                      <button
                        type="button"
                        onClick={() => cameraInputRef.current?.click()}
                        className="border-2 border-dashed border-[#0033A0] rounded-sm bg-[#F8FAFF] hover:bg-[#E8F0FE] text-xs font-medium text-[#0033A0] flex items-center justify-center gap-1"
                        data-testid="add-photo-button"
                      >
                        <Camera size={16} /> {t('aiSurvey.addPhoto')}
                      </button>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="border-2 border-dashed border-[#E2E8F0] rounded-sm bg-white hover:bg-[#F4F6F8] text-xs font-medium text-[#4A5568] flex items-center justify-center gap-1"
                        data-testid="add-file-button"
                      >
                        <Upload size={16} /> {t('aiSurvey.addFile')}
                      </button>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleReset} className="flex-1" data-testid="reset-button">
                    {t('aiSurvey.changeImage')}
                  </Button>
                  <Button
                    onClick={handleAnalyze}
                    disabled={analyzing}
                    className="flex-1 bg-[#0033A0] hover:bg-[#0047AB] text-white"
                    data-testid="analyze-button"
                  >
                    {analyzing ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                        {t('aiSurvey.analyzing')}
                      </span>
                    ) : (
                      <>
                        <Sparkle size={18} className="mr-2" weight="fill" />
                        {t('aiSurvey.analyzeAi')}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="mt-6 p-4 bg-[#F4F6F8] rounded-sm">
              <h4 className="text-sm font-semibold text-[#0A0F1A] mb-2">{t('aiSurvey.howItWorks')}</h4>
              <ol className="text-sm text-[#4A5568] space-y-1.5 list-decimal list-inside">
                <li>{t('aiSurvey.step1')}</li>
                <li>{t('aiSurvey.step2')}</li>
                <li>{t('aiSurvey.step3')}</li>
                <li>{t('aiSurvey.step4')}</li>
              </ol>
            </div>
          </CardContent>
        </Card>

        {/* Results Section */}
        <div className="space-y-6">
          {analyzing && (
            <Card className="border border-[#E2E8F0] rounded-sm">
              <CardContent className="p-8 text-center">
                <div className="animate-pulse space-y-4">
                  <div className="w-16 h-16 bg-[#E8F0FE] rounded-full flex items-center justify-center mx-auto">
                    <Sparkle size={28} className="text-[#0033A0]" weight="fill" />
                  </div>
                  <p className="font-medium text-[#0A0F1A]">{t('aiSurvey.analyzing')}</p>
                  <p className="text-sm text-[#4A5568]">
                    {t('aiSurvey.aiReadingNotes')}
                  </p>
                  <div className="flex justify-center gap-1">
                    {[0, 1, 2].map(i => (
                      <div key={i} className="w-2 h-2 bg-[#0033A0] rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {result && (
            <>
              {/* Success Banner */}
              <Card className="border border-green-200 bg-green-50 rounded-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <CheckCircle size={24} className="text-green-600" weight="fill" />
                  <div>
                    <p className="font-medium text-green-800">{result.message}</p>
                    <p className="text-sm text-green-600">{t('aiSurvey.leadAndQuoteCreated')}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Client Info */}
              <Card className="border border-[#E2E8F0] rounded-sm">
                <CardHeader className="border-b border-[#E2E8F0] pb-3 flex flex-row items-center justify-between gap-2">
                  <CardTitle className="font-outfit text-base font-semibold text-[#0A0F1A] flex items-center gap-2">
                    <User size={18} className="text-[#0033A0]" />
                    {isClientMissing ? 'Cliente non rilevato' : t('aiSurvey.clientCreated')}
                  </CardTitle>
                  {!isClientMissing && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => setClientDialogOpen(true)}
                      disabled={linkingClient}
                      data-testid="ai-change-client-btn"
                    >
                      <PencilSimple size={12} className="mr-1" /> Cambia
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="p-4 space-y-2">
                  {isClientMissing ? (
                    <div className="space-y-3">
                      <p className="text-sm text-[#4A5568]">
                        L'IA non ha trovato un nominativo nelle note. Aggiungi i dati del cliente per completare il preventivo.
                      </p>
                      <Button
                        type="button"
                        onClick={() => setClientDialogOpen(true)}
                        disabled={linkingClient}
                        className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
                        data-testid="ai-add-client-btn"
                      >
                        <User size={16} className="mr-2" />
                        Aggiungi cliente
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="font-medium text-[#0A0F1A]" data-testid="result-client-name">{result.client.name}</p>
                      {result.client.address && (
                        <p className="text-sm text-[#4A5568] flex items-center gap-2">
                          <MapPin size={14} /> {result.client.address}
                        </p>
                      )}
                      {result.client.phone && (
                        <p className="text-sm text-[#4A5568] flex items-center gap-2">
                          <Phone size={14} /> {result.client.phone}
                        </p>
                      )}
                      {result.client.email && (
                        <p className="text-sm text-[#4A5568] flex items-center gap-2">
                          <EnvelopeSimple size={14} /> {result.client.email}
                        </p>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {/* Products Found */}
              <Card className="border border-[#E2E8F0] rounded-sm">
                <CardHeader className="border-b border-[#E2E8F0] pb-3">
                  <CardTitle className="font-outfit text-base font-semibold text-[#0A0F1A] flex items-center gap-2">
                    <Package size={18} className="text-[#0033A0]" />
                    {t('aiSurvey.productsFound')} ({result.quote.items?.length || 0})
                  </CardTitle>
                  {result.pricing && (
                    <p className="text-xs text-[#4A5568] mt-1" data-testid="pricing-summary">
                      {result.pricing.listino + (result.pricing.libreria || 0) + result.pricing.documento} su {result.quote.items?.length || 0} voci già prezzate
                      {result.pricing.da_completare > 0 && <> · {result.pricing.da_completare} da completare</>}
                    </p>
                  )}
                </CardHeader>
                <CardContent className="p-4">
                  <div className="space-y-3">
                    {(result.quote.items || []).map((item, idx) => (
                      <div key={idx} className="flex items-start justify-between gap-2 p-3 bg-[#F4F6F8] rounded-sm" data-testid={`result-item-${idx}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm text-[#0A0F1A]">{item.product_name?.split(' - ')[0]}</p>
                            {item.measures_uncertain && (
                              <span
                                className="text-[10px] font-medium px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full flex items-center gap-1"
                                title="Misure poco leggibili - controlla prima di inviare"
                                data-testid={`uncertain-flag-${idx}`}
                              >
                                ⚠ Misure da verificare
                              </span>
                            )}
                            {item.price_source === 'listino' && (
                              <span
                                className="text-[10px] font-medium px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full"
                                title="Prezzo calcolato dal tuo listino in base a tipo, profilo e misure"
                                data-testid={`price-listino-${idx}`}
                              >
                                Prezzo da listino
                              </span>
                            )}
                            {item.price_source === 'libreria' && (
                              <span
                                className="text-[10px] font-medium px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full"
                                title="Prezzo dalla tua Libreria Prodotti Extra"
                                data-testid={`price-library-${idx}`}
                              >
                                Prezzo da libreria
                              </span>
                            )}
                            {item.price_source === 'da_completare' && (
                              <span
                                className="text-[10px] font-medium px-2 py-0.5 bg-slate-200 text-slate-700 rounded-full"
                                title="Prodotto non presente nel listino o misure mancanti: inserisci il prezzo nel preventivo"
                                data-testid={`price-missing-${idx}`}
                              >
                                Prezzo da completare
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-[#4A5568] mt-0.5">
                            {[
                              item.width > 0 && item.height > 0 ? `${formatDim(item.width)}x${formatDim(item.height)}mm` : null,
                              `Qtà: ${item.quantity}`,
                              item.material || null,
                              item.opening_type ? `Apertura: ${item.opening_type.replace(/_/g, '-')}` : null,
                            ].filter(Boolean).join(' | ')}
                          </p>
                          {item.price_detail && <p className="text-xs text-[#4A5568] mt-0.5">{item.price_detail}</p>}
                          {item.measures_warning && (
                            <p className="text-xs text-amber-800 mt-0.5" data-testid={`measures-warning-${idx}`}>⚠ {item.measures_warning}</p>
                          )}
                          {item.notes && <p className="text-xs text-[#4A5568] mt-0.5 italic">{item.notes}</p>}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-semibold text-[#0033A0] text-sm">{'\u20AC'}{item.total_price?.toFixed(2)}</span>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => openCorrect(idx)}
                            className="text-[#4A5568] hover:text-[#0033A0] hover:bg-[#E8F0FE] h-8 w-8 p-0"
                            title="Correggi — l'IA imparerà da questa correzione"
                            data-testid={`correct-item-${idx}`}
                          >
                            <PencilSimple size={14} />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 pt-3 border-t border-[#E2E8F0] flex items-center justify-between">
                    <span className="font-semibold text-[#0A0F1A]">{t('aiSurvey.totalInclIva')}</span>
                    <span className="text-lg font-bold text-[#0033A0]" data-testid="result-total">{'\u20AC'}{result.quote.total?.toFixed(2)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/quotes/${result.quote.id}`)}
                  className="flex-1 border-[#0033A0] text-[#0033A0]"
                  data-testid="view-quote-button"
                >
                  <Eye size={18} className="mr-2" />
                  {t('aiSurvey.viewQuote')}
                </Button>
                <Button
                  onClick={() => navigate(`/quotes/${result.quote.id}/edit`)}
                  className="flex-1 bg-[#0033A0] hover:bg-[#0047AB] text-white"
                  data-testid="edit-quote-button"
                >
                  {t('aiSurvey.editQuote')}
                </Button>
              </div>
            </>
          )}

          {!analyzing && !result && (
            <Card className="border border-[#E2E8F0] rounded-sm">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#F4F6F8] rounded-full flex items-center justify-center mx-auto">
                  <Sparkle size={28} className="text-[#4A5568]" />
                </div>
                <p className="font-medium text-[#0A0F1A] mt-4">{t('aiSurvey.noResult')}</p>
                <p className="text-sm text-[#4A5568] mt-1">
                  {t('aiSurvey.noResultDesc')}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Learn Dialog */}
      <Dialog open={learnOpen} onOpenChange={setLearnOpen}>
        <DialogContent className="max-w-2xl" data-testid="learn-vocab-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-outfit">
              <Sparkle size={20} className="text-[#0033A0]" weight="fill" />
              Impara nuove sigle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-sm text-[#4A5568]">
              L'IA ha trovato {learnQueue.length} {learnQueue.length === 1 ? 'sigla' : 'sigle'} che non riconosce.
              Confermando il significato, la prossima volta {learnQueue.length === 1 ? 'verrà riconosciuta' : 'verranno riconosciute'} automaticamente.
            </p>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {learnQueue.map((v, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-end p-3 bg-[#F4F6F8] rounded-sm" data-testid={`learn-row-${idx}`}>
                  <div className="col-span-3">
                    <Label className="text-[10px] text-[#4A5568]">Sigla</Label>
                    <Input
                      value={v.code}
                      readOnly
                      className="h-9 text-sm mt-1 font-mono bg-white font-semibold"
                      data-testid={`learn-code-${idx}`}
                    />
                  </div>
                  <div className="col-span-9 sm:col-span-5">
                    <Label className="text-[10px] text-[#4A5568]">Significato</Label>
                    <Input
                      placeholder="Es. Oblò a vetri"
                      value={v.name}
                      onChange={(e) => {
                        const arr = [...learnQueue];
                        arr[idx] = { ...arr[idx], name: e.target.value };
                        setLearnQueue(arr);
                      }}
                      className="h-9 text-sm mt-1"
                      data-testid={`learn-name-${idx}`}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <Label className="text-[10px] text-[#4A5568]">Tipo</Label>
                    <Select
                      value={v.product_type || '_none'}
                      onValueChange={(val) => {
                        const arr = [...learnQueue];
                        arr[idx] = { ...arr[idx], product_type: val === '_none' ? '' : val };
                        setLearnQueue(arr);
                      }}
                    >
                      <SelectTrigger className="h-9 text-xs mt-1" data-testid={`learn-type-${idx}`}><SelectValue placeholder="Qualsiasi" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none"><span className="text-[#A0AEC0] italic">Qualsiasi</span></SelectItem>
                        <SelectItem value="finestra">Finestra</SelectItem>
                        <SelectItem value="portafinestra">Portafinestra</SelectItem>
                        <SelectItem value="porta_ingresso">Porta d'ingresso</SelectItem>
                        <SelectItem value="scorrevole">Scorrevole</SelectItem>
                        <SelectItem value="persiana">Persiana</SelectItem>
                        <SelectItem value="tapparella">Tapparella</SelectItem>
                        <SelectItem value="zanzariera">Zanzariera</SelectItem>
                        <SelectItem value="altro">Altro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-5 sm:col-span-1">
                    <Label className="text-[10px] text-[#4A5568]">Ante</Label>
                    <Input
                      type="number" min="0" max="10"
                      value={v.num_ante}
                      onChange={(e) => {
                        const arr = [...learnQueue];
                        arr[idx] = { ...arr[idx], num_ante: parseInt(e.target.value) || 0 };
                        setLearnQueue(arr);
                      }}
                      className="h-9 text-sm mt-1 text-center"
                      data-testid={`learn-ante-${idx}`}
                    />
                  </div>
                  <div className="col-span-1 flex items-end h-full">
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => setLearnQueue(learnQueue.filter((_, i) => i !== idx))}
                      className="text-[#4A5568] hover:bg-red-50 h-9 w-9 p-0"
                      data-testid={`learn-skip-${idx}`}
                      title="Ignora questa sigla"
                    >
                      <X size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2 flex-col sm:flex-row">
            <Button
              variant="outline"
              onClick={() => { setLearnOpen(false); setLearnQueue([]); }}
              data-testid="learn-cancel"
            >
              Ignora tutto
            </Button>
            <Button
              onClick={saveLearnedVocab}
              disabled={savingLearn || learnQueue.filter(v => v.name && v.name.trim()).length === 0}
              className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
              data-testid="learn-save"
            >
              <BookmarkSimple size={16} className="mr-2" />
              {savingLearn ? 'Salvataggio...' : `Aggiungi al vocabolario (${learnQueue.filter(v => v.name && v.name.trim()).length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Correction Dialog */}
      <Dialog open={correctOpen} onOpenChange={setCorrectOpen}>
        <DialogContent className="max-w-xl" data-testid="correct-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-outfit">
              <PencilSimple size={20} className="text-[#0033A0]" />
              Correggi interpretazione IA
            </DialogTitle>
          </DialogHeader>
          {correctDraft && (
            <div className="space-y-3 pt-2">
              <p className="text-xs text-[#4A5568]">
                Correggi i campi sbagliati: il preventivo viene aggiornato e ricalcolato subito, e l'IA userà la correzione come esempio per le prossime analisi simili.
              </p>
              <div>
                <Label className="text-[10px] text-[#4A5568]">Nome prodotto</Label>
                <Input
                  value={correctDraft.product_name}
                  onChange={(e) => setCorrectDraft({ ...correctDraft, product_name: e.target.value })}
                  className="h-9 text-sm mt-1"
                  data-testid="correct-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] text-[#4A5568]">Tipo prodotto</Label>
                  <Select
                    value={correctDraft.product_type || '_none'}
                    onValueChange={(v) => setCorrectDraft({ ...correctDraft, product_type: v === '_none' ? '' : v })}
                  >
                    <SelectTrigger className="h-9 text-xs mt-1" data-testid="correct-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none"><span className="text-[#A0AEC0] italic">(nessuno)</span></SelectItem>
                      <SelectItem value="finestra">Finestra</SelectItem>
                      <SelectItem value="portafinestra">Portafinestra</SelectItem>
                      <SelectItem value="porta_ingresso">Porta d'ingresso</SelectItem>
                      <SelectItem value="scorrevole">Scorrevole</SelectItem>
                      <SelectItem value="persiana">Persiana</SelectItem>
                      <SelectItem value="tapparella">Tapparella</SelectItem>
                      <SelectItem value="zanzariera">Zanzariera</SelectItem>
                      <SelectItem value="altro">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] text-[#4A5568]">N° ante</Label>
                  <Input
                    type="number" min="0" max="10"
                    value={correctDraft.num_ante}
                    onChange={(e) => setCorrectDraft({ ...correctDraft, num_ante: parseInt(e.target.value) || 0 })}
                    className="h-9 text-sm mt-1 text-center"
                    data-testid="correct-ante"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-[10px] text-[#4A5568]">Larghezza (mm)</Label>
                  <Input
                    type="number" min="0"
                    value={correctDraft.width}
                    onChange={(e) => setCorrectDraft({ ...correctDraft, width: parseFloat(e.target.value) || 0 })}
                    className="h-9 text-sm mt-1"
                    data-testid="correct-width"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-[#4A5568]">Altezza (mm)</Label>
                  <Input
                    type="number" min="0"
                    value={correctDraft.height}
                    onChange={(e) => setCorrectDraft({ ...correctDraft, height: parseFloat(e.target.value) || 0 })}
                    className="h-9 text-sm mt-1"
                    data-testid="correct-height"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-[#4A5568]">Apertura</Label>
                  <Select
                    value={correctDraft.opening_type || '_none'}
                    onValueChange={(v) => setCorrectDraft({ ...correctDraft, opening_type: v === '_none' ? '' : v })}
                  >
                    <SelectTrigger className="h-9 text-xs mt-1" data-testid="correct-opening"><SelectValue placeholder="..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none"><span className="text-[#A0AEC0] italic">(nessuna)</span></SelectItem>
                      <SelectItem value="anta">Anta (battente)</SelectItem>
                      <SelectItem value="ribalta">Ribalta (vasistas)</SelectItem>
                      <SelectItem value="anta_ribalta">Anta-Ribalta</SelectItem>
                      <SelectItem value="scorrevole">Scorrevole</SelectItem>
                      <SelectItem value="scorrevole_alzante">Alzante-Scorrevole</SelectItem>
                      <SelectItem value="fisso">Fisso</SelectItem>
                      <SelectItem value="bilico">Bilico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] text-[#4A5568]">Quantità</Label>
                  <Input
                    type="number" min="1"
                    value={correctDraft.quantity}
                    onChange={(e) => setCorrectDraft({ ...correctDraft, quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                    className="h-9 text-sm mt-1 text-center"
                    data-testid="correct-quantity"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-[#4A5568]">Materiale</Label>
                  <Select
                    value={correctDraft.material || '_none'}
                    onValueChange={(v) => setCorrectDraft({ ...correctDraft, material: v === '_none' ? '' : v })}
                  >
                    <SelectTrigger className="h-9 text-xs mt-1" data-testid="correct-material"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none"><span className="text-[#A0AEC0] italic">(non indicato)</span></SelectItem>
                      {['PVC', 'Alluminio', 'Legno', 'Legno-Alluminio'].concat(
                        correctDraft.material && !['PVC', 'Alluminio', 'Legno', 'Legno-Alluminio'].includes(correctDraft.material)
                          ? [correctDraft.material] : []
                      ).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-[#4A5568]">Nota per l'IA (opzionale)</Label>
                <Input
                  placeholder="Es. Questo cliente usa sempre PVC, mai alluminio"
                  value={correctNote}
                  onChange={(e) => setCorrectNote(e.target.value)}
                  className="h-9 text-sm mt-1"
                  data-testid="correct-note"
                />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCorrectOpen(false)} data-testid="correct-cancel">Annulla</Button>
            <Button
              onClick={saveCorrection}
              disabled={savingCorrect}
              className="bg-[#0033A0] hover:bg-[#0047AB] text-white"
              data-testid="correct-save"
            >
              <Sparkle size={16} className="mr-2" weight="fill" />
              {savingCorrect ? 'Salvataggio...' : "Salva e aggiorna il preventivo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New / change client dialog (after AI analysis) */}
      <NewClientDialog
        open={clientDialogOpen}
        onOpenChange={setClientDialogOpen}
        defaults={{
          name: isClientMissing ? '' : (result?.client?.name || ''),
          phone: result?.client?.phone || '',
          email: result?.client?.email || '',
          address: result?.client?.address || '',
        }}
        onCreated={async (c) => {
          await linkClientToQuote(c);
        }}
      />
    </div>
  );
}

